import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import {
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  useCallbackRef,
  useFavoriteSecurities,
  useMixpanel,
  type AgGridMenuItems,
  type WatchListRowData,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import { useNavigateToDeepDive } from '../../hooks/useNavigateToDeepDive';
import { useNavigateToMultilegCombo } from '../../hooks/useNavigateToMultilegCombo';
import { getAvailableMultilegTypes } from '../../utils/getAvailableMultilegTypes';

export function useWatchlistContextMenuItems() {
  const mixpanel = useMixpanel();
  const { favoriteSecurities, setFavoriteSecurities } = useFavoriteSecurities();
  const navigateToDeepDive = useNavigateToDeepDive();
  const navigateToMultilegCombo = useNavigateToMultilegCombo();
  const { enableCFDs } = useFeatureFlag();

  return useCallbackRef((params: GetContextMenuItemsParams<WatchListRowData>): AgGridMenuItems<WatchListRowData> => {
    const security = params.node?.data?.security;
    if (!security) {
      return [];
    }

    mixpanel.track(MixpanelEvent.OpenSymbolMenu, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.Watchlist,
    });

    const isFavorite = favoriteSecurities.includes(security.Symbol);

    const toggleFavorite = () => {
      const next = isFavorite
        ? favoriteSecurities.filter(s => s !== security.Symbol)
        : [...favoriteSecurities, security.Symbol];
      setFavoriteSecurities(next);
      mixpanel.track(MixpanelEvent.FavoriteSymbol, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.Watchlist,
      });
    };

    const openDeepDive = () => {
      navigateToDeepDive(security, undefined, undefined, undefined);
    };

    const availableMultilegTypes = getAvailableMultilegTypes(security, enableCFDs);
    const createMultilegSubMenu: AgGridMenuItems<WatchListRowData> =
      availableMultilegTypes.length > 0
        ? [
            {
              name: 'Create New Multileg',
              icon: `<i class="ag-icon ${IconName.Plus}"/>`,
              subMenu: availableMultilegTypes.map(option => ({
                name: option.label,
                action: () => {
                  mixpanel.track(MixpanelEvent.CreateNewMultileg, {
                    [MixpanelEventProperty.Type]: option.type,
                    [MixpanelEventProperty.Source]: MixpanelEventSource.Watchlist,
                  });
                  navigateToMultilegCombo({ instrumentType: option.type, legSymbols: [security.Symbol] });
                },
              })),
            },
          ]
        : [];

    return [
      {
        name: 'Favorite Symbol',
        icon: `<i class="ag-icon ${isFavorite ? IconName.StarSolid : IconName.Star}"/>`,
        action: toggleFavorite,
      },
      {
        name: 'Open DeepDive',
        icon: `<i class="ag-icon ${IconName.Deepdive}"/>`,
        action: openDeepDive,
      },
      ...createMultilegSubMenu,
    ];
  });
}
